import React from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { useNextTrainings, NextTraining } from '@/features/home/hooks/useNextTrainings';
import { Plus } from 'lucide-react';
import calendarIcon from '@/assets/icons/calendar.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { EmptyStateCard } from '@/components/ui/EmptyStateCard';
import { getTrainingCategoryTranslationKey } from '@/constants/trainingCategories';

const NextTrainingsSection: React.FC = () => {
  const { t, currentLanguage } = useI18n();
  const { data: trainings, isLoading, error, refetch } = useNextTrainings();
  const location = useLocation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Get the date being displayed
  const getDisplayDate = () => {
    if (!trainings || trainings.length === 0) return '';
    
    const firstTrainingDate = new Date(trainings[0].start_time);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (firstTrainingDate.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (firstTrainingDate.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return firstTrainingDate.toLocaleDateString(currentLanguage === 'pt' ? 'pt-BR' : 'en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  // Forçar recarregamento quando o usuário retorna para a página Home
  useEffect(() => {
    if (location.pathname === '/home') {
      // Invalidar queries relacionadas para forçar recarregamento
      queryClient.invalidateQueries({ queryKey: ['next-trainings'] });
      queryClient.invalidateQueries({ queryKey: ['journey-highlights'] });
      queryClient.invalidateQueries({ queryKey: ['connection-suggestions'] });
      queryClient.invalidateQueries({ queryKey: ['top-posts'] });
      
      // Forçar refetch imediato
      refetch();
    }
  }, [location.pathname, queryClient, refetch]);

  const handleSeeAll = () => {
    navigate('/calendar');
  };

  const handleAddTraining = () => {
    // Navigate to calendar with add training modal open
    navigate('/calendar', {
      state: {
        openAddTrainingModal: true
      }
    });
  };

  // Função para traduzir o tipo de treino usando função centralizada
  const getTrainingTypeTranslation = (type: string) => {
    const translationKey = getTrainingCategoryTranslationKey(type);
    return t(`calendar.training_categories.${translationKey}`) || type;
  };

  const formatTime = (timeString: string) => {
    const date = new Date(timeString);
    // Usar locale baseado no idioma atual
    const locale = currentLanguage === 'pt' ? 'pt-BR' : 'en-US';
    // Garantir formato 12h com AM/PM
    return date.toLocaleTimeString(locale, { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    
    // Sempre usar o formato "02 Tue" (dia + dia da semana abreviado)
    const day = date.getDate().toString().padStart(2, '0');
    // Usar locale baseado no idioma atual
    const locale = currentLanguage === 'pt' ? 'pt-BR' : 'en-US';
    const weekday = date.toLocaleDateString(locale, { weekday: 'short' });
    
    return `${day} ${weekday}`;
  };

  if (isLoading) {
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <img src={calendarIcon} alt="Calendar" className="w-5 h-5 text-white" />
            <div>
              <h2 className="text-sm font-bold text-white">{t('home.next_trainings.title')}</h2>
            </div>
          </div>
          <button
            onClick={handleSeeAll}
            className="text-sm text-white hover:text-gray-300 transition-colors"
          >
            {t('home.next_trainings.see_all')}
          </button>
        </div>
        <div className="relative" style={{ maxWidth: 'calc(100vw - 20px)' }}>
          <div className="w-full overflow-x-auto pb-2 scrollbar-hide">
            <div className="flex space-x-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex-shrink-0 w-64 bg-[rgb(45,36,36)] rounded-lg p-4 animate-pulse">
                  <div className="mb-3">
                    <div className="h-6 bg-gray-700 rounded mb-2"></div>
                    <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <img src={calendarIcon} alt="Calendar" className="w-5 h-5 text-white" />
            <div>
              <h2 className="text-sm font-bold text-white">{t('home.next_trainings.title')}</h2>
            </div>
          </div>
          <button
            onClick={handleSeeAll}
            className="text-sm text-white hover:text-gray-300 transition-colors"
          >
            {t('home.next_trainings.see_all')}
          </button>
        </div>
        <div className="text-red-400 text-center py-8">
          Error loading trainings. Please try again.
        </div>
      </div>
    );
  }

  const hasTrainings = trainings && trainings.length > 0;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div>
            <h2 className="text-sm font-bold text-white">{t('home.next_trainings.title')}</h2>
            {getDisplayDate() && (
              <p className="text-xs text-gray-400 font-normal">{getDisplayDate()}</p>
            )}
          </div>
        </div>
        <button
          onClick={handleSeeAll}
          className="text-xs text-white underline font-roboto font-normal hover:text-gray-300 transition-colors"
        >
          {t('home.next_trainings.see_all')}
        </button>
      </div>

      {!hasTrainings ? (
        <EmptyStateCard
          title={t('home.next_trainings.no_trainings_title')}
          description={t('home.next_trainings.no_trainings_description')}
          buttonText={t('home.next_trainings.add_new_training')}
          onButtonClick={handleAddTraining}
        />
      ) : (
        <>
          <div className="relative max-w-full mx-auto">
            <div className="w-full overflow-x-auto pb-2 scrollbar-hide">
              <div className="flex space-x-4">
                {trainings.slice(0, 3).map((training) => {
                  const dateInfo = formatDate(training.start_time);
                  const startTime = formatTime(training.start_time);
                  const endTime = formatTime(training.end_time);
                  
                  return (
                    <div key={training.id} className="flex-shrink-0 w-64 bg-[rgb(45,36,36)] rounded-lg p-4">
                      <div className="mb-3">
                        <div className="text-sm font-bold text-white mb-2 font-roboto">
                          {getTrainingTypeTranslation(training.type)} - {training.modality}
                        </div>
                        <div className="text-xs text-gray-300 font-roboto flex items-center space-x-2">
                          <img src={calendarIcon} alt="Calendar" className="w-4 h-4" />
                          <span>{dateInfo} - {startTime} {t('time_connector')} {endTime}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Add new training button */}
          <div className="mt-4">
            <button
              onClick={handleAddTraining}
              className="w-full text-white px-6 py-3 rounded-full border border-white transition-colors flex items-center justify-center font-roboto text-sm hover:bg-white hover:text-gray-800"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('home.next_trainings.add_new_training')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default NextTrainingsSection;
